import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQueryWithIntercept } from "../connection/api";

const ENDPOINT_PREFIX = "content";

type GetTopicsByCodeArgs = { topicIds: string[] };

export type Topic = {
    _id: string;
    id: string;
    topicId: string;
    module?: string;
    statements?: string[];
    tips?: string[];
    videos?: string[];
    dictionary?: {
        en?: { name?: string; description?: string; status?: string };
        [k: string]: { name?: string; description?: string; status?: string } | undefined;
    };
    createdAt?: string;
    updatedAt?: string;
};
type GetTopicsByCodeResponse = {
    data: {
        topics: Topic[];
    };
};

export type UtilsLogType =
    | "recalculateModuleProgress"
    | "syncModules"
    | "syncTopics"
    | "syncLevels"
    | "syncStatements"
    | "syncAssessments"
    | "translateModules"
    | "translateTopics"
    | "translateLevels"
    | "translateStatements"
    | "translateAssessments"
    | "importWebModules";

export type UtilsLog = {
    _id: string;
    type: UtilsLogType;
    moduleCode?: string;
    updatedClients?: number;
    updatedRecords?: number;
    translatedRecords?: number;
    checkedRecords?: number;
    completedModulesCount?: number;
    completedTopicsCount?: number;
    completedLevelsCount?: number;
    completedStatementsCount?: number;
    completedAssessmentsCount?: number;
    totalLevelsToTranslate?: number;
    remainingLevelsToTranslate?: number;
    totalStatementsToTranslate?: number;
    remainingStatementsToTranslate?: number;
    totalAssessmentsToTranslate?: number;
    remainingAssessmentsToTranslate?: number;
    fetchedModules?: number;
    fetchedTopics?: number;
    fetchedLevels?: number;
    fetchedStatements?: number;
    fetchedAssessments?: number;
    createdModules?: string[];
    changedModules?: string[];
    createdTopics?: string[];
    changedTopics?: string[];
    createdLevels?: string[];
    changedLevels?: string[];
    createdStatements?: string[];
    changedStatements?: string[];
    createdAssessments?: string[];
    changedAssessments?: string[];
    translationLanguages?: string[];
    moduleTranslationLanguages?: Array<{
        moduleId: string;
        generatedLanguages: string[];
        checkedLanguages: string[];
    }>;
    topicTranslationLanguages?: Array<{
        topicId: string;
        moduleId: string;
        generatedLanguages: string[];
        checkedLanguages: string[];
    }>;
    levelTranslationLanguages?: Array<{
        u_level_id: string;
        moduleId: string;
        generatedLanguages: string[];
        checkedLanguages: string[];
    }>;
    statementTranslationLanguages?: Array<{
        u_stmt_id: string;
        topicId: string;
        generatedLanguages: string[];
        checkedLanguages: string[];
    }>;
    assessmentTranslationLanguages?: Array<{
        as_code: string;
        as_topic: string;
        generatedLanguages: string[];
        checkedLanguages: string[];
    }>;
    geminiTranslationCorrections?: Array<{
        moduleId: string;
        language: string;
        fields: string[];
        generated?: {
            name?: string;
            description?: string;
            descriptionLong?: string;
        };
        checked?: {
            name?: string;
            description?: string;
            descriptionLong?: string;
        };
    }>;
    geminiTopicTranslationCorrections?: Array<{
        topicId: string;
        moduleId: string;
        language: string;
        fields: string[];
        generated?: {
            name?: string;
            description?: string;
            friendly_name?: string;
            friendly_desc?: string;
        };
        checked?: {
            name?: string;
            description?: string;
            friendly_name?: string;
            friendly_desc?: string;
        };
    }>;
    geminiLevelTranslationCorrections?: Array<{
        u_level_id: string;
        moduleId: string;
        language: string;
        fields: string[];
        generated?: {
            level_name?: string | null;
            tip_before?: string | null;
            tip_after?: string | null;
        };
        checked?: {
            level_name?: string | null;
            tip_before?: string | null;
            tip_after?: string | null;
        };
    }>;
    geminiStatementTranslationCorrections?: Array<{
        u_stmt_id: string;
        topicId: string;
        language: string;
        fields: string[];
        generated?: {
            negfemale?: string;
            negmale?: string;
            posfemale?: string;
            posmale?: string;
        };
        checked?: {
            negfemale?: string;
            negmale?: string;
            posfemale?: string;
            posmale?: string;
        };
    }>;
    geminiAssessmentTranslationCorrections?: Array<{
        as_code: string;
        as_topic: string;
        language: string;
        fields: string[];
        generated?: {
            name?: string;
            description?: string;
            question?: string;
            message?: string;
            options?: string[];
        };
        checked?: {
            name?: string;
            description?: string;
            question?: string;
            message?: string;
            options?: string[];
        };
    }>;
    levelNumericMarkerRepairs?: Array<{
        u_level_id: string;
        moduleId: string;
        language: string;
        stage: string;
        field: string;
        sourceNumbers: string[];
        translatedNumbers: string[];
        repairedNumbers: string[];
        originalValue?: string | null;
        repairedValue?: string | null;
    }>;
    errorMessages?: string[];
    warningMessages?: string[];
    dryRun?: boolean;
    archivedLevels?: string[];
    archivedStatements?: string[];
    status: "success" | "error";
    createdAt: string;
};

type GetUtilsLogsResponse = {
    data: {
        logs: UtilsLog[];
        pagination: {
            totalDocs: number;
            limit: number;
            page: number;
            totalPages: number;
            hasNextPage: boolean;
            hasPrevPage: boolean;
            nextPage: number | null;
            prevPage: number | null;
            pagingCounter: number;
        };
    };
};

type GetUtilsLogsArgs = {
    type?: UtilsLogType;
    page?: number;
    limit?: number;
};

type RecalculateModuleProgressBody = {
    data: {
        moduleCode: string;
    };
};

type RecalculateModuleProgressResponse = {
    message?: string;
    data?: {
        moduleCode?: string;
        updatedClients?: number;
    };
};

type SyncModulesTranslationsResponse = {
    message?: string;
};

type SyncTopicsTranslationsResponse = {
    message?: string;
};

type SyncLevelsTranslationsResponse = {
    message?: string;
};

type SyncStatementsTranslationsResponse = {
    message?: string;
};

type SyncAssessmentsTranslationsResponse = {
    message?: string;
};

type TranslateModulesResponse = {
    message?: string;
    data?: {
        fetchedModules?: number;
        translatedRecords?: number;
        checkedRecords?: number;
        completedModules?: number;
        geminiCorrectionCount?: number;
        moduleTranslationLanguages?: Array<{
            moduleId: string;
            generatedLanguages: string[];
            checkedLanguages: string[];
        }>;
        languages?: string[];
        errorMessages?: string[];
    };
};

type TranslateTopicsResponse = {
    message?: string;
    data?: {
        fetchedTopics?: number;
        translatedRecords?: number;
        checkedRecords?: number;
        completedTopics?: number;
        geminiCorrectionCount?: number;
        topicTranslationLanguages?: Array<{
            topicId: string;
            moduleId: string;
            generatedLanguages: string[];
            checkedLanguages: string[];
        }>;
        languages?: string[];
        errorMessages?: string[];
    };
};

type TranslateLevelsResponse = {
    message?: string;
    data?: {
        totalLevelsToTranslate?: number;
        fetchedLevels?: number;
        remainingLevelsToTranslate?: number;
        translatedRecords?: number;
        checkedRecords?: number;
        completedLevels?: number;
        geminiCorrectionCount?: number;
        numericMarkerRepairCount?: number;
        levelTranslationLanguages?: Array<{
            u_level_id: string;
            moduleId: string;
            generatedLanguages: string[];
            checkedLanguages: string[];
        }>;
        languages?: string[];
        errorMessages?: string[];
    };
};

type TranslateStatementsResponse = {
    message?: string;
    data?: {
        totalStatementsToTranslate?: number;
        fetchedStatements?: number;
        remainingStatementsToTranslate?: number;
        translatedRecords?: number;
        checkedRecords?: number;
        completedStatements?: number;
        geminiCorrectionCount?: number;
        statementTranslationLanguages?: Array<{
            u_stmt_id: string;
            topicId: string;
            generatedLanguages: string[];
            checkedLanguages: string[];
        }>;
        languages?: string[];
        errorMessages?: string[];
    };
};

type TranslateAssessmentsResponse = {
    message?: string;
    data?: {
        totalAssessmentsToTranslate?: number;
        fetchedAssessments?: number;
        remainingAssessmentsToTranslate?: number;
        translatedRecords?: number;
        checkedRecords?: number;
        completedAssessments?: number;
        geminiCorrectionCount?: number;
        assessmentTranslationLanguages?: Array<{
            as_code: string;
            as_topic: string;
            generatedLanguages: string[];
            checkedLanguages: string[];
        }>;
        languages?: string[];
        errorMessages?: string[];
    };
};

// ---- web modules authored in Google Sheets (server: services/webContent) ------------------------

export type WebContentSource = {
    _id: string;
    module: string;
    sheetId: string;
    remindersSheetId?: string | null;
    name: string;
    description?: string;
    category?: string | null;
    uiGroup?: string | null;
    enabled: boolean;
    reportEmails: string[];
    lastImportAt?: string | null;
    lastImportStatus?: "success" | "error" | "skipped" | null;
    lastImportSummary?: string | null;
    createdAt?: string;
    updatedAt?: string;
};

export type WebContentIssue = {
    severity: "error" | "warning";
    code: string;
    tab: "levels" | "statements" | "sentences" | "workbook" | "reminders";
    row: number | null;
    id: string | null;
    message: string;
};

export type WebModuleImportReport = {
    module: string;
    sheetId: string;
    dryRun: boolean;
    status: "success" | "error";
    startedAt: string;
    durationMs: number;
    fetched: { levels: number; statements: number; sentences?: number };
    ready: { levels: number; statements: number; sentences?: number; days: number };
    levels: { created: string[]; changed: string[]; unchanged: number; archived: string[] };
    statements: { created: string[]; changed: string[]; unchanged: number; archived: string[] };
    sentences?: { created: string[]; changed: string[]; unchanged: number; archived: string[] };
    moduleDoc: "created" | "changed" | "unchanged" | "skipped";
    errors: WebContentIssue[];
    warnings: WebContentIssue[];
};

export type WebSourceLevel = {
    u_level_id: string;
    day: number | null;
    level_seq: number | null;
    status: string | null;
    archivedAt: string | null;
    level_name: string;
    tip_before: string | null;
    is_major: string | null;
    game_type: string | null;
    num_correct: number | null;
    activity_type: string | null;
    activity_ref: string | null;
    assess_topic: string | null;
    topics: string[];
    color: string | null;
};

export type WebSourceStatement = {
    u_stmt_id: string;
    topicId: string;
    status: string | null;
    archivedAt: string | null;
    negmale: string;
    posmale: string;
};

/** A Huppert Sentence row (tab `sentences`): the stem, the correct and the wrong word, the prompt. */
export type WebSourceSentence = {
    u_sent_id: string;
    belief: string;
    topicId: string;
    status: string | null;
    archivedAt: string | null;
    sentence: string;
    answer_correct: string;
    answer_wrong: string;
    prompt: string | null;
};

type GetWebSourcesResponse = { data: WebContentSource[] };
type WebSourceResponse = { data: WebContentSource };
export type UpsertWebSourceBody = {
    module: string;
    sheetId: string;
    remindersSheetId?: string | null;
    name?: string;
    description?: string;
    category?: string | null;
    uiGroup?: string | null;
    enabled?: boolean;
    reportEmails?: string[];
};
export type ImportWebModulesArgs = { dryRun: boolean; module?: string };
export type ImportWebModulesResponse = { message?: string; data?: { dryRun: boolean; reports: WebModuleImportReport[] } };
export type GetWebSourceLevelsResponse = {
    data: { source: WebContentSource; levels: WebSourceLevel[]; statements: WebSourceStatement[]; sentences?: WebSourceSentence[]; lastLog: UtilsLog | null };
};

export const contentApi = createApi({
    reducerPath: "contentApi",
    baseQuery: baseQueryWithIntercept,
    tagTypes: ["contentTopics", "webSources", "webSourceLevels"],
    endpoints: build => ({
        getWebSources: build.query<GetWebSourcesResponse, void>({
            query: () => ({ url: `${ENDPOINT_PREFIX}/web-sources`, method: "GET" }),
            providesTags: [{ type: "webSources" as const, id: "LIST" }],
        }),
        upsertWebSource: build.mutation<WebSourceResponse, UpsertWebSourceBody>({
            query: body => ({ url: `${ENDPOINT_PREFIX}/web-sources`, method: "POST", body }),
            invalidatesTags: [{ type: "webSources" as const, id: "LIST" }],
        }),
        deleteWebSource: build.mutation<WebSourceResponse, string>({
            query: module => ({ url: `${ENDPOINT_PREFIX}/web-sources/${encodeURIComponent(module)}`, method: "DELETE" }),
            invalidatesTags: [{ type: "webSources" as const, id: "LIST" }],
        }),
        importWebModules: build.mutation<ImportWebModulesResponse, ImportWebModulesArgs>({
            query: body => ({ url: `${ENDPOINT_PREFIX}/import-web-modules`, method: "POST", body }),
            invalidatesTags: (_result, _error, args) =>
                (args.dryRun
                    ? [{ type: "webSourceLevels" as const, id: args.module ?? "ALL" }]
                    : [{ type: "webSources" as const, id: "LIST" }, { type: "webSourceLevels" as const, id: "ALL" }]),
        }),
        getWebSourceLevels: build.query<GetWebSourceLevelsResponse, string>({
            query: module => ({ url: `${ENDPOINT_PREFIX}/web-sources/${encodeURIComponent(module)}/levels`, method: "GET" }),
            providesTags: (_result, _error, module) => [
                { type: "webSourceLevels" as const, id: module },
                { type: "webSourceLevels" as const, id: "ALL" },
            ],
        }),
        getTopicsByCodeData: build.query<GetTopicsByCodeResponse, GetTopicsByCodeArgs>({
            query: ({ topicIds }) => ({
                url: `${ENDPOINT_PREFIX}/topics-by-code`,
                params: { topicIds: topicIds.join(",") },
            }),
            providesTags: result =>
                (result?.data?.topics?.length
                    ? [...result.data.topics.map(t => ({ type: "contentTopics" as const, id: t.topicId })), { type: "contentTopics" as const, id: "LIST" }]
                    : [{ type: "contentTopics" as const, id: "LIST" }]),
        }),
        getUtilsLogs: build.query<GetUtilsLogsResponse, GetUtilsLogsArgs | void>({
            query: args => {
                const params: Record<string, string | number> = {};
                if (args?.type) params.type = args.type;
                if (args?.page) params.page = args.page;
                if (args?.limit) params.limit = args.limit;
                return {
                    url: `${ENDPOINT_PREFIX}/utils-logs`,
                    method: "GET",
                    params: Object.keys(params).length ? params : undefined,
                };
            },
        }),
        recalculateModuleProgress: build.mutation<RecalculateModuleProgressResponse, RecalculateModuleProgressBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/recalculate-module-progress`,
                method: "POST",
                body: data.data,
            }),
        }),
        syncModulesTranslations: build.mutation<SyncModulesTranslationsResponse, void>({
            query: () => ({
                url: `${ENDPOINT_PREFIX}/sync-modules-translations`,
                method: "POST",
            }),
        }),
        syncTopicsTranslations: build.mutation<SyncTopicsTranslationsResponse, void>({
            query: () => ({
                url: `${ENDPOINT_PREFIX}/sync-topics-translations`,
                method: "POST",
            }),
        }),
        syncLevelsTranslations: build.mutation<SyncLevelsTranslationsResponse, void>({
            query: () => ({
                url: `${ENDPOINT_PREFIX}/sync-levels-translations`,
                method: "POST",
            }),
        }),
        syncStatementsTranslations: build.mutation<SyncStatementsTranslationsResponse, void>({
            query: () => ({
                url: `${ENDPOINT_PREFIX}/sync-statements-translations`,
                method: "POST",
            }),
        }),
        syncAssessmentsTranslations: build.mutation<SyncAssessmentsTranslationsResponse, void>({
            query: () => ({
                url: `${ENDPOINT_PREFIX}/sync-assessments-translations`,
                method: "POST",
            }),
        }),
        translateModules: build.mutation<TranslateModulesResponse, void>({
            query: () => ({
                url: `${ENDPOINT_PREFIX}/translate-modules`,
                method: "POST",
            }),
        }),
        translateTopics: build.mutation<TranslateTopicsResponse, void>({
            query: () => ({
                url: `${ENDPOINT_PREFIX}/translate-topics`,
                method: "POST",
            }),
        }),
        translateLevels: build.mutation<TranslateLevelsResponse, void>({
            query: () => ({
                url: `${ENDPOINT_PREFIX}/translate-levels`,
                method: "POST",
            }),
        }),
        translateStatements: build.mutation<TranslateStatementsResponse, void>({
            query: () => ({
                url: `${ENDPOINT_PREFIX}/translate-statements`,
                method: "POST",
            }),
        }),
        translateAssessments: build.mutation<TranslateAssessmentsResponse, void>({
            query: () => ({
                url: `${ENDPOINT_PREFIX}/translate-assessments`,
                method: "POST",
            }),
        }),
    }),
});

export const {
    useGetWebSourcesQuery,
    useUpsertWebSourceMutation,
    useDeleteWebSourceMutation,
    useImportWebModulesMutation,
    useGetWebSourceLevelsQuery,
    useGetTopicsByCodeDataQuery,
    useGetUtilsLogsQuery,
    useRecalculateModuleProgressMutation,
    useSyncModulesTranslationsMutation,
    useSyncTopicsTranslationsMutation,
    useSyncLevelsTranslationsMutation,
    useSyncStatementsTranslationsMutation,
    useSyncAssessmentsTranslationsMutation,
    useTranslateModulesMutation,
    useTranslateTopicsMutation,
    useTranslateLevelsMutation,
    useTranslateStatementsMutation,
    useTranslateAssessmentsMutation,
} = contentApi;
